import { useState } from 'react';
import {
  Box,
  Card,
  CircularProgress,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';

import {
  type ExtractedFact,
  formatFactValue,
  interpolateCareRecipient,
} from '@karehero/models';
import { formatDateTime } from '@karehero/helpers/datetime';
import { Icon } from '@/components/atoms/Icon/Icon';
import SideDrawer from '@/components/molecules/SideDrawer/SideDrawer';

export interface KeyDocumentFactsDrawerProps {
  open: boolean;
  documentTitle?: string;
  facts: ExtractedFact[];
  isLoading?: boolean;
  careRecipientName?: string;
  extractedAt?: string | null;
  questionFormMap?: Record<string, string[]>;
  onClose: () => void;
  onConfirm: (factId: string) => void;
  onDismiss: (factId: string) => void;
}

const getFactLabel = (fact: ExtractedFact): string =>
  fact.mappings.length > 0 ? fact.mappings[0].label : fact.factName;

const FactCard = ({
  fact,
  careRecipientName,
  formPaths,
  onConfirm,
  onDismiss,
}: {
  fact: ExtractedFact;
  careRecipientName?: string;
  formPaths?: string[];
  onConfirm?: (factId: string) => void;
  onDismiss?: (factId: string) => void;
}) => {
  const isConfirmed = Boolean(fact.confirmedAt);
  const isDismissed = Boolean(fact.dismissedAt);
  const isActioned = isConfirmed || isDismissed;
  const label = interpolateCareRecipient(getFactLabel(fact), careRecipientName);

  return (
    <Card
      key={fact.id}
      elevation={0}
      sx={[
        isDismissed && { opacity: 0.5 },
        isConfirmed && {
          borderLeft: 3,
          borderColor: 'success.main',
        },
      ]}
    >
      <Box sx={{ p: 2 }}>
        <Stack
          direction='row'
          spacing={1}
          sx={{
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Stack sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant='body2' sx={{ fontWeight: 600 }}>
              {label}
            </Typography>
            <Typography
              variant='body1'
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {formatFactValue(fact)}
            </Typography>
            {formPaths &&
              formPaths.map((path) => (
                <Typography key={path} variant='caption' color='text.secondary'>
                  → {interpolateCareRecipient(path, careRecipientName)}
                </Typography>
              ))}
            {isConfirmed && (
              <Typography variant='caption' color='success.main'>
                Confirmed
              </Typography>
            )}
            {isDismissed && (
              <Typography variant='caption' color='text.secondary'>
                Dismissed
              </Typography>
            )}
          </Stack>
          {!isActioned && onConfirm && onDismiss && (
            <Stack direction='row' spacing={0.5}>
              <IconButton
                size='small'
                color='success'
                onClick={() => onConfirm(fact.id)}
                aria-label='Confirm'
              >
                <Icon icon='check' fontSize={16} />
              </IconButton>
              <IconButton
                size='small'
                color='error'
                onClick={() => onDismiss(fact.id)}
                aria-label='Dismiss'
              >
                <Icon icon='xmark' fontSize={16} />
              </IconButton>
            </Stack>
          )}
        </Stack>
      </Box>
    </Card>
  );
};

const KeyDocumentFactsDrawer = ({
  open,
  documentTitle,
  facts,
  isLoading,
  careRecipientName,
  extractedAt,
  questionFormMap,
  onClose,
  onConfirm,
  onDismiss,
}: KeyDocumentFactsDrawerProps) => {
  const [showActioned, setShowActioned] = useState(false);

  const pendingFacts = facts.filter((f) => !f.confirmedAt && !f.dismissedAt);
  const actionedFacts = facts.filter((f) => f.confirmedAt || f.dismissedAt);

  const getFormPaths = (fact: ExtractedFact): string[] | undefined => {
    if (!questionFormMap || fact.mappings.length === 0) return undefined;
    const paths = questionFormMap[fact.mappings[0].questionId];
    return paths?.length ? paths : undefined;
  };

  return (
    <SideDrawer
      open={open}
      onClose={onClose}
      header={
        <Stack>
          <Typography variant='h3' color='primary.main'>
            {documentTitle ?? 'Extracted Facts'}
          </Typography>
          {extractedAt && (
            <Typography variant='caption' color='text.secondary'>
              Extracted {formatDateTime(extractedAt)}
            </Typography>
          )}
        </Stack>
      }
    >
      {isLoading && (
        <Stack sx={{ alignItems: 'center', py: 4 }}>
          <CircularProgress size={28} />
        </Stack>
      )}

      {!isLoading && facts.length === 0 && (
        <Typography variant='body2' color='text.secondary'>
          No facts extracted from this document.
        </Typography>
      )}

      {!isLoading && facts.length > 0 && (
        <Stack spacing={1}>
          {pendingFacts.map((fact) => (
            <FactCard
              key={fact.id}
              fact={fact}
              careRecipientName={careRecipientName}
              formPaths={getFormPaths(fact)}
              onConfirm={onConfirm}
              onDismiss={onDismiss}
            />
          ))}

          {actionedFacts.length > 0 && (
            <>
              <Typography
                variant='body2'
                color='text.secondary'
                onClick={() => setShowActioned((prev) => !prev)}
                sx={{ cursor: 'pointer', py: 0.5, userSelect: 'none' }}
              >
                <Icon
                  icon={showActioned ? 'chevron-down' : 'chevron-right'}
                  fontSize={12}
                />{' '}
                {actionedFacts.length} fact
                {actionedFacts.length !== 1 ? 's' : ''} confirmed or dismissed
              </Typography>
              {showActioned &&
                actionedFacts.map((fact) => (
                  <FactCard
                    key={fact.id}
                    fact={fact}
                    careRecipientName={careRecipientName}
                    formPaths={getFormPaths(fact)}
                  />
                ))}
            </>
          )}

          {pendingFacts.length === 0 &&
            actionedFacts.length > 0 &&
            !showActioned && (
              <Typography variant='body2' color='text.secondary'>
                All facts have been reviewed.
              </Typography>
            )}
        </Stack>
      )}
    </SideDrawer>
  );
};

KeyDocumentFactsDrawer.displayName = 'KeyDocumentFactsDrawer';
export default KeyDocumentFactsDrawer;
